import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { MenuData } from '../types';
import type { MenuProfile } from '../types';

export const downloadImagesZip = async (
  data: MenuData,
  onProgress?: (percent: number) => void,
  opts?: { profile?: MenuProfile; currency?: string },
) => {
  const zip = new JSZip();
  const imagesFolder = zip.folder("images");
  if (!imagesFolder) return;

  const sanitize = (name: string) => name.replace(/[\/\\:*?"<>|]/g, "").trim().replace(/\s+/g, " ");

  // Build authenticated URL — media routes require a JWT token
  const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';
  const token = localStorage.getItem('dm_jwt') ?? '';

  const toAbsoluteUrl = (url: string): string => {
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${API_BASE}${url}${token ? `${sep}token=${encodeURIComponent(token)}` : ''}`;
  };

  // ── Bundle both Excel templates into the ZIP ──
  if (opts?.profile) {
    try {
      const { exportRelationalTemplate1Buffer, exportRelationalTemplate2Buffer } = await import('./MenuExcelTemplates');
      const cur = opts.currency ?? '';
      const t1Buf = exportRelationalTemplate1Buffer(opts.profile, cur);
      const t2Buf = exportRelationalTemplate2Buffer(opts.profile, cur);
      const brandName = sanitize(opts.profile.brandName || 'Menu');
      const menuName = sanitize(opts.profile.name || 'Export');
      zip.file(`Template_1_Detailed_${brandName}_${menuName}.xlsx`, t1Buf);
      zip.file(`Template_2_Aggregator_${brandName}_${menuName}.xlsx`, t2Buf);
    } catch { /* excel generation failed — still download images */ }
  }

  const fetchImage = async (url: string) => {
    try {
      const response = await fetch(toAbsoluteUrl(url));
      if (!response.ok) return null;
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      let ext = 'jpg';
      if (blob.type.includes('png')) ext = 'png';
      else if (blob.type.includes('webp')) ext = 'webp';
      
      return { data: arrayBuffer, ext };
    } catch (e) { return null; }
  };

  const promises: Promise<void>[] = [];
  let completed = 0;
  
  let totalItems = 0;
  if (data.logo) totalItems++;
  data.categories.forEach(c => c.items.forEach(i => { if (i.image) totalItems++; }));

  const updateProgress = () => {
      completed++;
      if (onProgress && totalItems > 0) onProgress(Math.round((completed / totalItems) * 100));
  };

  if (data.logo) {
      const logoFolder = zip.folder("logo");
      if (logoFolder) {
          promises.push((async () => {
              const imgData = await fetchImage(data.logo!);
              if (imgData) logoFolder.file(`logo.${imgData.ext}`, imgData.data);
              updateProgress();
          })());
      }
  }

  for (const category of data.categories) {
    const catName = sanitize(category.name.en || 'Untitled');
    const catFolder = imagesFolder.folder(catName);
    if (!catFolder) continue;

    const usedNames = new Set<string>();

    for (const item of category.items) {
      if (!item.image) continue;
      
      let baseName = sanitize(item.name.en || 'Item');
      let finalName = baseName;
      let counter = 2;
      while (usedNames.has(finalName)) { finalName = `${baseName}_${counter++}`; }
      usedNames.add(finalName);

      promises.push((async () => {
        const imgData = await fetchImage(item.image!);
        if (imgData) catFolder.file(`${finalName}.${imgData.ext}`, imgData.data);
        updateProgress();
      })());
    }
  }

  await Promise.all(promises);
  const content = await zip.generateAsync({ type: "blob" });
  saveAs(content, "menu_images.zip");
};
